<template>
	<v-container fluid class="stamp-admin pa-6">
		<div class="d-flex align-center mb-4">
			<div class="stamp-admin-title">QR 스탬프투어 관리</div>
			<v-select
				v-model="selectedBusiness"
				:items="businesses"
				item-text="title"
				item-value="id"
				label="사업지"
				outlined
				dense
				hide-details
				return-object
				style="max-width: 260px"
				class="ml-6"
				@change="onBusinessChange"
			/>
			<v-spacer />
			<v-btn outlined color="teal" class="mr-2" :disabled="!selectedBusiness" @click="openStats">
				<v-icon left small>mdi-chart-bar</v-icon> 참여 현황
			</v-btn>
			<v-btn outlined color="deep-purple" class="mr-2" :disabled="!selectedBusiness" @click="openFestivalManager">
				<v-icon left small>mdi-calendar-star</v-icon> 행사 안내 관리
			</v-btn>
			<v-btn outlined color="indigo" class="mr-2" :disabled="!selectedBusiness" @click="openNoticeManager">
				<v-icon left small>mdi-bullhorn-outline</v-icon> 공지사항 관리
			</v-btn>
			<v-btn color="primary" depressed :disabled="!selectedBusiness" @click="openCreate">
				<v-icon left small>mdi-plus</v-icon> 신규 투어 등록
			</v-btn>
		</div>

		<!-- 사업지 메인 행사 이미지 (스탬프 페이지 헤더 하단 배너) -->
		<v-card v-if="selectedBusiness" outlined class="mb-4 pa-3">
			<div class="d-flex align-center flex-wrap">
				<div class="mr-4">
					<div class="font-weight-bold">스탬프 페이지 메인 행사 이미지</div>
					<div class="caption grey--text">고객 스탬프투어 페이지 상단(헤더 아래)에 표시됩니다. · 사업지: {{ selectedBusiness.title }}</div>
				</div>
				<v-spacer />
				<v-img
					v-if="mainBannerPreview"
					:src="mainBannerPreview"
					max-width="260"
					max-height="90"
					contain
					class="grey lighten-3 rounded mr-3"
				/>
				<div v-else class="caption grey--text mr-3">등록된 이미지가 없습니다.</div>
				<v-file-input
					v-model="mainBannerFile"
					label="이미지 선택"
					accept="image/*"
					prepend-icon="mdi-image"
					outlined
					dense
					hide-details
					show-size
					style="max-width: 280px"
					class="mr-2"
					@change="onMainBannerChange"
				/>
				<v-btn color="primary" depressed small :loading="mainBannerSaving" class="mr-1" @click="saveMainBanner">저장</v-btn>
				<v-btn v-if="mainBannerPreview || mainBannerFile" text small color="error" @click="removeMainBanner">제거</v-btn>
			</div>
		</v-card>

		<v-data-table :headers="headers" :items="filteredTours" :loading="loading" :items-per-page="20" class="elevation-1">
			<template v-slot:[`item.type`]="{ item }">
				{{ item.type === 'stamp-auto' ? '현장 참여(자동)' : item.type === 'quiz' ? 'OX 퀴즈' : '스탬프 투어' }}
			</template>
			<template v-slot:[`item.period`]="{ item }">
				{{ item.sdate | moment('YYYY-MM-DD HH:mm') }} ~ {{ item.edate | moment('YYYY-MM-DD HH:mm') }}
			</template>
			<template v-slot:[`item.groupCount`]="{ item }"> {{ (item.reserveday_groups || []).filter(g => g.useYn).length }}개 </template>
			<template v-slot:[`item.eventTitle`]="{ item }">
				{{ item.event ? item.event.title : '-' }}
			</template>
			<template v-slot:[`item.actions`]="{ item }">
				<v-btn x-small outlined color="primary" class="mr-1" @click="openEdit(item)">수정</v-btn>
				<v-btn x-small outlined color="teal" class="mr-1" @click="openEntryQR(item)">참여 QR</v-btn>
				<v-btn x-small outlined color="teal" class="mr-1" @click="openEntryPage(item)">참여 URL</v-btn>
				<v-btn x-small icon color="teal" class="mr-1" title="참여 URL 복사" @click="copyEntryUrl(item)">
					<v-icon x-small>mdi-content-copy</v-icon>
				</v-btn>
				<v-btn x-small outlined color="indigo" class="mr-1" @click="openGroupQR(item)">스탬프 QR</v-btn>
				<v-btn x-small outlined color="error" @click="removeTour(item)">삭제</v-btn>
			</template>
		</v-data-table>

		<!-- 등록/수정 다이얼로그 -->
		<v-dialog v-model="editDialog" persistent max-width="640" scrollable>
			<v-card>
				<v-card-title>
					{{ form.id ? '스탬프 투어 수정' : '신규 스탬프 투어 등록' }}
					<v-spacer />
					<v-btn icon @click="editDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<v-text-field v-model="form.title" label="투어명 (예: 동물행복런 스탬프 투어)" outlined dense />
					<v-text-field
						v-model="form.groupcode"
						label="행사 코드"
						outlined
						dense
						hint="같은 행사 코드를 가진 투어들이 하나의 스탬프투어 페이지에 함께 노출됩니다."
						persistent-hint
						class="mb-2"
					/>
					<v-select
						v-model="form.type"
						:items="[
							{ text: '스탬프 투어 (QR 스캔 적립)', value: 'stamp' },
							{ text: '현장 참여 (화면 진입 시 자동 적립)', value: 'stamp-auto' },
							{ text: 'OX 퀴즈 (미션 성공 시 적립)', value: 'quiz' },
						]"
						label="투어 유형"
						outlined
						dense
					/>
					<v-row dense>
						<v-col cols="6">
							<v-text-field v-model="form.sdate" label="운영 시작" type="datetime-local" outlined dense />
						</v-col>
						<v-col cols="6">
							<v-text-field v-model="form.edate" label="운영 종료" type="datetime-local" outlined dense />
						</v-col>
					</v-row>
					<v-row dense>
						<v-col cols="6">
							<v-text-field
								v-model="form.staffCode"
								label="운영요원 확인 코드 (경품 수령용)"
								outlined
								dense
								hint="연결 이벤트 없이 입력하면 완주 후 '경품 수령 확인(코드 입력)'으로 참가 완료됩니다. 비우면 '참가 완료하기' 버튼으로 바로 완료."
								persistent-hint
							/>
						</v-col>
						<v-col cols="6">
							<v-select v-model="form.event" :items="eventItems" label="연결 이벤트 (룰렛/경품)" outlined dense clearable />
						</v-col>
					</v-row>
					<v-text-field v-model="form.endLink" label="만족도 조사 URL" outlined dense />
					<v-text-field
						v-model.number="form.stampSort"
						label="투어 노출 순서"
						type="number"
						outlined
						dense
						hint="고객 화면에서 숫자가 작은 투어부터 위에 노출됩니다. (같으면 등록 순)"
						persistent-hint
						class="mb-2"
					/>

					<div class="font-weight-bold mb-1">스탬프 투어 상단 이미지</div>
					<div class="caption grey--text mb-2">
						스탬프 투어 참여 페이지 맨 위에 노출되는 이미지입니다. (권장: 가로형 배너 이미지)
					</div>
					<v-file-input
						v-model="form.mainimgFile"
						label="이미지 선택"
						accept="image/*"
						prepend-icon="mdi-image"
						outlined
						dense
						show-size
						:clearable="!form.mainimgPreview"
						@change="onMainimgChange"
					/>
					<div v-if="form.mainimgPreview" class="mb-3">
						<v-img :src="form.mainimgPreview" max-height="180" contain class="grey lighten-3 rounded" />
						<div class="text-right mt-1">
							<v-btn x-small text color="error" @click="removeMainimg"> <v-icon x-small left>mdi-close</v-icon> 이미지 제거 </v-btn>
						</div>
					</div>

					<!-- 스탬프 지점 (일반/현장참여 투어) -->
					<template v-if="form.type !== 'quiz'">
						<div class="d-flex align-center mt-2 mb-2">
							<div class="font-weight-bold">스탬프 지점 (QR 발급 단위)</div>
							<v-spacer />
							<v-btn x-small outlined color="primary" @click="form.groups.push({ id: null, title: '', useYn: true })">
								<v-icon x-small left>mdi-plus</v-icon> 지점 추가
							</v-btn>
						</div>
						<div v-for="(g, gi) in form.groups" :key="gi" class="d-flex align-center mb-1">
							<v-btn icon x-small :disabled="gi === 0" title="위로" @click="moveGroup(gi, -1)">
								<v-icon small>mdi-arrow-up</v-icon>
							</v-btn>
							<v-btn icon x-small :disabled="gi === form.groups.length - 1" title="아래로" class="mr-1" @click="moveGroup(gi, 1)">
								<v-icon small>mdi-arrow-down</v-icon>
							</v-btn>
							<v-text-field v-model="g.title" :label="`지점명 ${gi + 1} (예: 1코스, 휘루)`" outlined dense hide-details class="mr-2" />
							<v-switch v-model="g.useYn" label="노출" hide-details dense class="mt-0 pt-0" />
						</div>
						<div class="caption grey--text mt-1">
							* 지점 QR코드는 저장 시 자동 발급됩니다. 지점 삭제 대신 노출을 꺼주세요.<br />
							* 화살표로 바꾼 순서대로 고객 화면에 노출됩니다. (순서를 바꿔도 발급된 QR코드는 그대로입니다)
						</div>
					</template>

					<!-- OX 퀴즈 설정 -->
					<template v-else>
						<div class="d-flex align-center mt-2 mb-2">
							<div class="font-weight-bold">OX 퀴즈 문제</div>
							<v-chip x-small class="ml-2" outlined>{{ quizForm.questions.length }}문항</v-chip>
							<v-spacer />
							<v-btn x-small outlined color="primary" @click="addQuizQuestion"> <v-icon x-small left>mdi-plus</v-icon> 문항 추가 </v-btn>
						</div>
						<v-row dense class="mb-1">
							<v-col cols="6">
								<v-text-field
									v-model.number="quizForm.passScore"
									label="미션 성공(합격) 정답 수"
									type="number"
									outlined
									dense
									hide-details
									hint="이 개수 이상 맞히면 스탬프 적립"
									persistent-hint
								/>
							</v-col>
							<v-col cols="6" class="d-flex align-center">
								<v-switch v-model="quizForm.shuffle" label="문제 순서 랜덤 섞기" hide-details dense class="mt-0 pt-0" />
							</v-col>
						</v-row>
						<div v-for="(q, qi) in quizForm.questions" :key="qi" class="quiz-q-row pa-2 mb-2">
							<div class="d-flex align-center mb-1">
								<span class="font-weight-bold mr-2">{{ qi + 1 }}.</span>
								<v-btn-toggle v-model="q.ox" mandatory dense class="mr-2">
									<v-btn small value="O" :color="q.ox === 'O' ? 'success' : ''">O</v-btn>
									<v-btn small value="X" :color="q.ox === 'X' ? 'error' : ''">X</v-btn>
								</v-btn-toggle>
								<v-spacer />
								<v-btn icon x-small color="error" @click="quizForm.questions.splice(qi, 1)"><v-icon small>mdi-delete</v-icon></v-btn>
							</div>
							<v-text-field v-model="q.question" label="지문 (문제)" outlined dense hide-details class="mb-1" />
							<v-text-field v-model="q.explain" label="한 줄 해설 (선택 후 노출)" outlined dense hide-details />
						</div>
						<div class="caption grey--text mt-1">
							* 미션 성공 시 현장 담당자가 소지한 스탬프 QR(관리 목록 [스탬프 QR]에서 출력)을 참가자가 휴대폰 [QR스캔]으로 찍으면 1개가
							적립됩니다. (완주 카운트/룰렛/참가완료는 스탬프 투어와 동일하게 동작)
						</div>
					</template>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="editDialog = false">취소</v-btn>
					<v-btn color="primary" depressed :loading="saving" @click="saveTour">저장</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- 참여 현황 다이얼로그 -->
		<v-dialog v-model="statsDialog" max-width="1180" scrollable>
			<v-card>
				<v-card-title>
					스탬프투어 참여 현황
					<span v-if="selectedBusiness" class="ml-2 subtitle-2 grey--text">({{ selectedBusiness.title }})</span>
					<v-spacer />
					<v-btn icon @click="statsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="d-flex align-center flex-wrap mb-3">
						<v-select
							v-model="statsForm.groupcode"
							:items="groupcodeItems"
							label="행사 코드"
							outlined
							dense
							hide-details
							style="max-width: 220px"
							class="mr-2"
						/>
						<v-text-field
							v-model="statsForm.from"
							label="시작일"
							type="date"
							outlined
							dense
							hide-details
							style="max-width: 170px"
							class="mr-2"
						/>
						<v-text-field
							v-model="statsForm.to"
							label="종료일"
							type="date"
							outlined
							dense
							hide-details
							style="max-width: 170px"
							class="mr-2"
						/>
						<v-btn color="primary" depressed :loading="statsLoading" class="mr-2" @click="fetchStats">조회</v-btn>
						<v-btn outlined color="green darken-2" :disabled="statsRows.length === 0" @click="downloadStatsExcel">
							<v-icon left small>mdi-microsoft-excel</v-icon> 엑셀 다운로드
						</v-btn>
					</div>
					<div class="caption grey--text mb-2">
						일자는 한국 시간 기준입니다. 참여자 = 해당 일자에 스탬프를 1개 이상 받은 인원 / 완주 = 노출 중인 지점을 모두 모은 인원 /
						참가완료 = 경품 수령(이벤트 연결 투어) 또는 참가 완료 버튼을 누른 인원(이벤트 없는 투어). 현장 참여(자동) 투어의 참여자 수는
						현장 인증 인원입니다.
					</div>
					<v-data-table
						:headers="statsHeaders"
						:items="statsRows"
						:loading="statsLoading"
						dense
						disable-pagination
						hide-default-footer
						no-data-text="조회된 참여 내역이 없습니다."
						:item-class="r => (r.isTotal ? 'stamp-stats-total' : '')"
					/>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- 스탬프 지점 QR 선택 다이얼로그 -->
		<v-dialog v-model="groupQRDialog" max-width="420">
			<v-card>
				<v-card-title>
					스탬프 QR 다운로드
					<v-spacer />
					<v-btn icon @click="groupQRDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<v-list dense>
						<v-list-item v-for="g in groupQRList" :key="g.id" @click="showGroupQR(g)">
							<v-list-item-icon><v-icon>mdi-qrcode</v-icon></v-list-item-icon>
							<v-list-item-content>
								<v-list-item-title>{{ g.title }}</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- 행사 안내(로그인 팝업) 관리 다이얼로그 -->
		<v-dialog v-model="festivalDialog" persistent max-width="920" scrollable>
			<v-card>
				<v-card-title>
					행사 안내 관리
					<span v-if="selectedBusiness" class="ml-2 subtitle-2 grey--text">({{ selectedBusiness.title }})</span>
					<v-spacer />
					<v-btn icon @click="festivalDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="caption grey--text mb-2">
						여기 등록한 행사는 고객 스탬프투어 <b>참여 정보 입력(로그인) 화면</b>에 <b>안내 팝업</b>으로 노출됩니다. (순서값이 큰 것부터)
					</div>

					<!-- 행사 입력 폼 -->
					<div class="pa-3 mb-3" style="border: 1px solid #e0e0e0; border-radius: 6px">
						<div class="font-weight-bold mb-2">{{ festivalForm.id ? '행사 수정' : '새 행사 등록' }}</div>
						<v-row dense>
							<v-col cols="12" sm="8">
								<v-text-field
									v-model="festivalForm.title"
									label="행사 제목 (예: 동물행복페스타)"
									outlined
									dense
									hide-details
									class="mb-2"
								/>
								<v-text-field
									v-model="festivalForm.dateText"
									label="날짜 (예: 10.17(토)~10.18(일))"
									outlined
									dense
									hide-details
									class="mb-2"
								/>
								<v-text-field v-model="festivalForm.link" label="링크 URL (클릭 시 이동)" outlined dense hide-details class="mb-2" />
							</v-col>
							<v-col cols="12" sm="4">
								<div class="caption grey--text mb-1">행사 이미지 (아이콘/썸네일)</div>
								<v-img
									v-if="festivalForm.imagePreview"
									:src="festivalForm.imagePreview"
									height="96"
									contain
									class="grey lighten-3 rounded mb-1"
								/>
								<v-file-input
									v-model="festivalForm.newImageFile"
									label="이미지 선택"
									accept="image/*"
									prepend-icon="mdi-image"
									outlined
									dense
									hide-details
									show-size
									@change="onFestivalImageChange"
								/>
							</v-col>
						</v-row>
						<div class="d-flex align-center mt-2">
							<v-text-field
								v-model.number="festivalForm.sort"
								label="순서"
								type="number"
								outlined
								dense
								hide-details
								style="max-width: 120px"
								class="mr-3"
							/>
							<v-switch v-model="festivalForm.useYn" label="노출" hide-details dense class="mt-0 pt-0" />
							<v-spacer />
							<v-btn v-if="festivalForm.id" text small class="mr-1" @click="resetFestivalForm">취소</v-btn>
							<v-btn color="primary" depressed small :loading="festivalSaving" @click="saveFestival">
								{{ festivalForm.id ? '수정 저장' : '등록' }}
							</v-btn>
						</div>
					</div>

					<!-- 행사 목록 -->
					<v-data-table
						:headers="festivalHeaders"
						:items="festivals"
						:loading="festivalLoading"
						:items-per-page="10"
						dense
						class="elevation-0 notice-table"
					>
						<template v-slot:[`item.image`]="{ item }">
							<v-img v-if="item.image" :src="mediaUrl(item.image.url)" width="44" height="44" cover class="rounded my-1" />
							<span v-else class="caption grey--text">-</span>
						</template>
						<template v-slot:[`item.link`]="{ item }">
							<a v-if="item.link" :href="item.link" target="_blank" rel="noopener" class="caption">{{ item.link }}</a>
							<span v-else class="caption grey--text">-</span>
						</template>
						<template v-slot:[`item.useYn`]="{ item }">
							<v-chip x-small :color="item.useYn ? 'teal' : 'grey'" dark>{{ item.useYn ? '노출' : '숨김' }}</v-chip>
						</template>
						<template v-slot:[`item.actions`]="{ item }">
							<v-btn x-small outlined color="primary" class="mr-1" @click="editFestival(item)">수정</v-btn>
							<v-btn x-small outlined color="error" @click="removeFestival(item)">삭제</v-btn>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- 공지사항(안내) 관리 다이얼로그 -->
		<v-dialog v-model="noticeDialog" persistent max-width="1080" scrollable>
			<v-card>
				<v-card-title>
					공지사항 관리
					<span v-if="selectedBusiness" class="ml-2 subtitle-2 grey--text">({{ selectedBusiness.title }})</span>
					<v-spacer />
					<v-btn icon @click="noticeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="caption grey--text mb-2">
						여기 등록한 공지는 고객 스탬프투어 페이지의 <b>[안내]</b> 탭에 노출됩니다. (노출 순서: 순서값이 큰 것부터)
					</div>

					<!-- 공지 입력 폼 -->
					<div class="pa-3 mb-3" style="border: 1px solid #e0e0e0; border-radius: 6px">
						<div class="font-weight-bold mb-2">{{ noticeForm.id ? '공지 수정' : '새 공지 등록' }}</div>
						<v-text-field v-model="noticeForm.title" label="제목" outlined dense hide-details class="mb-2" />
						<v-textarea v-model="noticeForm.content" label="내용" outlined dense hide-details rows="3" auto-grow class="mb-2" />

						<!-- 이미지 첨부 (고객 페이지에서 이미지로 표시) -->
						<v-file-input
							v-model="noticeForm.newImageFiles"
							label="이미지 첨부 (여러 장 가능)"
							accept="image/*"
							prepend-icon="mdi-image-multiple"
							multiple
							outlined
							dense
							show-size
							small-chips
							hide-details
							class="mb-2"
						/>
						<div v-if="noticeForm.images.length" class="d-flex flex-wrap mb-2">
							<div v-for="(img, ii) in noticeForm.images" :key="'img' + img.id" class="notice-thumb mr-2 mb-2">
								<v-img :src="mediaUrl(img.url)" width="72" height="72" cover class="rounded" />
								<v-btn icon x-small class="notice-thumb-del" @click="removeExistingImage(ii)">
									<v-icon x-small>mdi-close-circle</v-icon>
								</v-btn>
							</div>
						</div>

						<!-- 파일 첨부 (고객 페이지에서 다운로드) -->
						<v-file-input
							v-model="noticeForm.newFileFiles"
							label="파일 첨부 (다운로드용, 여러 개 가능)"
							prepend-icon="mdi-paperclip"
							multiple
							outlined
							dense
							show-size
							small-chips
							hide-details
							class="mb-2"
						/>
						<div v-if="noticeForm.files.length" class="mb-2">
							<v-chip
								v-for="(f, fi) in noticeForm.files"
								:key="'file' + f.id"
								class="mr-2 mb-1"
								close
								small
								label
								@click:close="removeExistingFile(fi)"
							>
								<v-icon x-small left>mdi-file</v-icon>{{ f.name }}
							</v-chip>
						</div>

						<div class="d-flex align-center">
							<v-text-field
								v-model.number="noticeForm.sort"
								label="순서"
								type="number"
								outlined
								dense
								hide-details
								style="max-width: 120px"
								class="mr-3"
							/>
							<v-switch v-model="noticeForm.useYn" label="노출" hide-details dense class="mt-0 pt-0" />
							<v-spacer />
							<v-btn v-if="noticeForm.id" text small class="mr-1" @click="resetNoticeForm">취소</v-btn>
							<v-btn color="primary" depressed small :loading="noticeSaving" @click="saveNotice">
								{{ noticeForm.id ? '수정 저장' : '등록' }}
							</v-btn>
						</div>
					</div>

					<!-- 공지 목록 -->
					<v-data-table
						:headers="noticeHeaders"
						:items="notices"
						:loading="noticeLoading"
						:items-per-page="10"
						dense
						class="elevation-0 notice-table"
					>
						<template v-slot:[`item.useYn`]="{ item }">
							<v-chip x-small :color="item.useYn ? 'teal' : 'grey'" dark>{{ item.useYn ? '노출' : '숨김' }}</v-chip>
						</template>
						<template v-slot:[`item.content`]="{ item }">
							<span class="notice-content-cell">{{ item.content }}</span>
						</template>
						<template v-slot:[`item.attach`]="{ item }">
							<span v-if="(item.images || []).length" class="mr-2 caption">
								<v-icon x-small>mdi-image</v-icon> {{ item.images.length }}
							</span>
							<span v-if="(item.files || []).length" class="caption"> <v-icon x-small>mdi-paperclip</v-icon> {{ item.files.length }} </span>
							<span v-if="!(item.images || []).length && !(item.files || []).length" class="caption grey--text">-</span>
						</template>
						<template v-slot:[`item.actions`]="{ item }">
							<v-btn x-small outlined color="primary" class="mr-1" @click="editNotice(item)">수정</v-btn>
							<v-btn x-small outlined color="error" @click="removeNotice(item)">삭제</v-btn>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-dialog>

		<QRDialog :setdialog="QRDialogData" />
	</v-container>
</template>

<script>
import QRDialog from '@/components/dialog/QRDialog.vue'

export default {
	name: 'stampTourManagement',
	components: { QRDialog },
	data() {
		return {
			loading: false,
			saving: false,
			tours: [],
			events: [],
			businesses: [],
			selectedBusiness: null,
			// 사업지 메인 행사 이미지(배너)
			mainBannerFile: null,
			mainBannerPreview: '',
			mainBannerRemove: false,
			mainBannerSaving: false,
			headers: [
				{ text: '행사 코드', value: 'groupcode', width: '11%' },
				{ text: '투어명', value: 'title' },
				{ text: '유형', value: 'type', width: '11%' },
				{ text: '운영 기간', value: 'period', width: '20%' },
				{ text: '순서', value: 'stampSort', width: '6%' },
				{ text: '지점', value: 'groupCount', width: '6%' },
				{ text: '연결 이벤트', value: 'eventTitle', width: '12%' },
				{ text: '관리', value: 'actions', width: '22%', sortable: false },
			],
			editDialog: false,
			form: this.emptyForm(),
			quizForm: { shuffle: true, passScore: 0, questions: [] },
			// 참여 현황
			statsDialog: false,
			statsLoading: false,
			statsForm: { groupcode: '', from: '', to: '' },
			statsRows: [],
			statsDetailRows: [],
			statsHeaders: [
				{ text: '일자', value: 'date', width: 100 },
				{ text: '투어', value: 'title' },
				{ text: '참여자', value: 'participants', align: 'end', width: 70 },
				{ text: '지점별 스탬프', value: 'groupText', sortable: false },
				{ text: '완주', value: 'completed', align: 'end', width: 60 },
				{ text: '참가완료', value: 'finished', align: 'end', width: 80 },
				{ text: '추첨', value: 'draws', align: 'end', width: 60 },
				{ text: '당첨', value: 'winners', align: 'end', width: 60 },
				{ text: '경품 수령', value: 'receipts', align: 'end', width: 80 },
			],
			groupQRDialog: false,
			groupQRList: [],
			groupQRTourTitle: '',
			QRDialogData: { dialog: false, code: '', title: '' },
			// 공지사항 관리
			noticeDialog: false,
			noticeLoading: false,
			noticeSaving: false,
			notices: [],
			noticeHeaders: [
				{ text: '순서', value: 'sort', width: 70 },
				{ text: '제목', value: 'title', width: 220 },
				{ text: '내용', value: 'content' },
				{ text: '첨부', value: 'attach', width: 100, sortable: false },
				{ text: '노출', value: 'useYn', width: 80 },
				{ text: '관리', value: 'actions', width: 150, sortable: false },
			],
			noticeForm: this.emptyNoticeForm(),
			// 행사 안내(로그인 팝업) 관리
			festivalDialog: false,
			festivalLoading: false,
			festivalSaving: false,
			festivals: [],
			festivalHeaders: [
				{ text: '순서', value: 'sort', width: 70 },
				{ text: '이미지', value: 'image', width: 70, sortable: false },
				{ text: '행사 제목', value: 'title', width: 200 },
				{ text: '날짜', value: 'dateText', width: 180 },
				{ text: '링크', value: 'link' },
				{ text: '노출', value: 'useYn', width: 80 },
				{ text: '관리', value: 'actions', width: 150, sortable: false },
			],
			festivalForm: this.emptyFestivalForm(),
		}
	},
	computed: {
		eventItems() {
			return this.events.map(e => ({ text: e.title, value: e.id }))
		},
		// 선택된 사업지에 등록된 행사 코드 목록 (참여 현황 조회용)
		groupcodeItems() {
			return [...new Set(this.filteredTours.map(t => t.groupcode).filter(x => x))]
		},
		// 선택된 사업지의 투어만 노출
		filteredTours() {
			if (!this.selectedBusiness) return []
			return this.tours.filter(t => t.business && t.business.id === this.selectedBusiness.id)
		},
	},
	async created() {
		await this.fetchBusinesses()
		await this.fetchTours()
		await this.fetchEvents()
	},
	methods: {
		emptyForm() {
			return {
				id: null,
				title: '',
				groupcode: '',
				type: 'stamp',
				sdate: '',
				edate: '',
				staffCode: '',
				endLink: '',
				stampSort: 0, // 투어 노출 순서 (작은 값 먼저)
				event: null,
				groups: [],
				business: null, // 연결 사업지 id
				mainimg: null, // 기존에 저장된 이미지 id
				mainimgFile: null, // 새로 선택한 파일
				mainimgPreview: '', // 미리보기 URL
			}
		},
		emptyNoticeForm() {
			return {
				id: null,
				title: '',
				content: '',
				useYn: true,
				sort: 0,
				images: [], // 기존 이미지 [{id,name,url,mime}]
				files: [], // 기존 첨부파일 [{id,name,url,mime,size}]
				newImageFiles: [], // 새로 선택한 이미지 File[]
				newFileFiles: [], // 새로 선택한 첨부 File[]
			}
		},
		async fetchBusinesses() {
			try {
				const res = await this.$store.dispatch('businesses', { show: true })
				this.businesses = res.data.businesses
				if (this.businesses.length > 0) {
					this.selectedBusiness = this.businesses[0]
					this.loadMainBanner()
				}
			} catch (err) {
				console.log({ err })
			}
		},
		onBusinessChange() {
			// filteredTours 는 computed 로 자동 갱신됨
			this.loadMainBanner()
		},
		// 선택된 사업지의 메인 행사 이미지 미리보기 로드
		loadMainBanner() {
			this.mainBannerFile = null
			this.mainBannerRemove = false
			const reg = this.selectedBusiness && this.selectedBusiness.registration
			this.mainBannerPreview = reg ? this.mediaUrl(reg.url) : ''
		},
		onMainBannerChange(file) {
			if (file) {
				this.mainBannerPreview = URL.createObjectURL(file)
				this.mainBannerRemove = false
			}
		},
		removeMainBanner() {
			this.mainBannerFile = null
			this.mainBannerPreview = ''
			this.mainBannerRemove = true
		},
		async saveMainBanner() {
			if (!this.selectedBusiness) return
			this.mainBannerSaving = true
			try {
				let registrationId
				if (this.mainBannerFile) {
					const up = await this.$store.dispatch('uploadRest', { file: this.mainBannerFile })
					registrationId = up.id
				} else if (this.mainBannerRemove) {
					registrationId = null
				} else {
					// 변경 없음
					this.mainBannerSaving = false
					alert('변경할 이미지를 선택해주세요.')
					return
				}
				const res = await this.$store.dispatch('updateBusinessRegistration', {
					id: this.selectedBusiness.id,
					registration: registrationId,
				})
				// 로컬 갱신
				const reg = res.data.updateBusiness.business.registration
				this.selectedBusiness.registration = reg
				const bi = this.businesses.findIndex(b => b.id === this.selectedBusiness.id)
				if (bi > -1) this.businesses[bi].registration = reg
				this.mainBannerFile = null
				this.mainBannerRemove = false
				this.mainBannerPreview = reg ? this.mediaUrl(reg.url) : ''
				alert('메인 행사 이미지가 저장되었습니다.')
			} catch (err) {
				console.log({ err })
				alert('이미지 저장 중 오류가 발생하였습니다.')
			} finally {
				this.mainBannerSaving = false
			}
		},
		async fetchTours() {
			this.loading = true
			try {
				const res = await this.$store.dispatch('stampReservedays', { type_in: ['stamp', 'stamp-auto', 'quiz'] })
				this.tours = res.data.reservedays
			} catch (err) {
				console.log({ err })
			} finally {
				this.loading = false
			}
		},
		async fetchEvents() {
			try {
				const res = await this.$store.dispatch('events', {})
				this.events = res.data.events
			} catch (err) {
				console.log({ err })
			}
		},
		openCreate() {
			if (!this.selectedBusiness) {
				alert('먼저 사업지를 선택해주세요.')
				return
			}
			this.form = this.emptyForm()
			this.form.business = this.selectedBusiness.id
			this.quizForm = { shuffle: true, passScore: 0, questions: [] }
			this.editDialog = true
		},
		openEdit(item) {
			this.form = {
				id: item.id,
				title: item.title,
				groupcode: item.groupcode,
				type: item.type,
				sdate: item.sdate ? this.$moment(item.sdate).format('YYYY-MM-DDTHH:mm') : '',
				edate: item.edate ? this.$moment(item.edate).format('YYYY-MM-DDTHH:mm') : '',
				staffCode: item.staffCode || '',
				endLink: item.endLink || '',
				stampSort: item.stampSort || 0,
				event: item.event ? item.event.id : null,
				// 저장된 순서(sort) → 등록 순(id)으로 정렬해 표시
				groups: [...(item.reserveday_groups || [])]
					.sort((a, b) => (a.sort || 0) - (b.sort || 0) || Number(a.id) - Number(b.id))
					.map(g => ({ id: g.id, title: g.title, useYn: g.useYn })),
				business: item.business ? item.business.id : this.selectedBusiness ? this.selectedBusiness.id : null,
				mainimg: item.mainimg ? item.mainimg.id : null,
				mainimgFile: null,
				mainimgPreview: item.mainimg ? this.$store.state.backServer + item.mainimg.url : '',
			}
			// 퀴즈 설정 복원
			const q = item.quiz || {}
			this.quizForm = {
				shuffle: q.shuffle != null ? q.shuffle : true,
				passScore: q.passScore != null ? q.passScore : 0,
				questions: Array.isArray(q.questions)
					? q.questions.map(x => ({ ox: x.ox || 'O', question: x.question || '', explain: x.explain || '' }))
					: [],
			}
			this.editDialog = true
		},
		addQuizQuestion() {
			this.quizForm.questions.push({ ox: 'O', question: '', explain: '' })
		},
		onMainimgChange(file) {
			if (file) {
				this.form.mainimgPreview = URL.createObjectURL(file)
			}
		},
		removeMainimg() {
			this.form.mainimgFile = null
			this.form.mainimg = null
			this.form.mainimgPreview = ''
		},
		async saveTour() {
			if (!this.form.title || !this.form.groupcode) {
				alert('투어명과 행사 코드를 입력해주세요.')
				return
			}
			if (!this.form.sdate || !this.form.edate) {
				alert('운영 기간을 입력해주세요.')
				return
			}
			this.saving = true
			try {
				// 상단 이미지 처리: 새 파일을 선택했으면 업로드 후 id 사용, 아니면 기존 id 유지(제거 시 null)
				let mainimgId = this.form.mainimg
				if (this.form.mainimgFile) {
					const up = await this.$store.dispatch('uploadRest', { file: this.form.mainimgFile })
					mainimgId = up.id
				}
				const isQuiz = this.form.type === 'quiz'
				// 퀴즈 데이터 구성 (빈 지문 제외)
				let quizData = null
				if (isQuiz) {
					const questions = this.quizForm.questions
						.filter(q => q.question && q.question.trim())
						.map(q => ({ ox: q.ox || 'O', question: q.question.trim(), explain: (q.explain || '').trim() }))
					if (questions.length === 0) {
						alert('OX 퀴즈 문항을 1개 이상 입력해주세요.')
						this.saving = false
						return
					}
					quizData = {
						shuffle: !!this.quizForm.shuffle,
						passScore: Number(this.quizForm.passScore) || Math.ceil(questions.length / 2),
						questions,
					}
				}
				const data = {
					title: this.form.title,
					groupcode: this.form.groupcode.trim(),
					type: this.form.type,
					sdate: new Date(this.form.sdate),
					edate: new Date(this.form.edate),
					staffCode: this.form.staffCode,
					endLink: this.form.endLink,
					status: 1,
					event: this.form.event,
					mainimg: mainimgId,
					business: this.form.business,
					quiz: quizData,
					stampSort: Number(this.form.stampSort) || 0,
				}
				let tourId = this.form.id
				if (tourId) {
					await this.$store.dispatch('updateStampTour', { id: tourId, ...data })
				} else {
					const res = await this.$store.dispatch('createStampTour', data)
					tourId = res.data.createReserveday.reserveday.id
				}
				if (isQuiz) {
					// 퀴즈 투어는 적립용 지점 1개가 필요 → 없으면 자동 생성
					const hasGroup = (this.form.groups || []).some(g => g.id) || (this.form.groups || []).length > 0
					if (!hasGroup) {
						await this.$store.dispatch('createReservedayGroup', {
							title: 'OX 퀴즈',
							reserveday: tourId,
							useYn: true,
							type: 'stamp',
						})
					}
				} else {
					// 스탬프 지점(그룹) 저장 - 신규는 생성(QR 자동 발급), 기존은 수정
					// 화면에 보이는 순서대로 sort(1,2,3...) 를 함께 저장
					let order = 0
					for (const g of this.form.groups) {
						if (!g.title) continue
						order += 1
						if (g.id) {
							await this.$store.dispatch('updateStampGroup', {
								id: g.id,
								title: g.title,
								useYn: g.useYn,
								sort: order,
							})
						} else {
							await this.$store.dispatch('createStampGroup', {
								title: g.title,
								reserveday: tourId,
								useYn: g.useYn,
								type: 'stamp',
								sort: order,
							})
						}
					}
				}
				this.editDialog = false
				await this.fetchTours()
			} catch (err) {
				console.log({ err })
				alert('저장 중 오류가 발생하였습니다.')
			} finally {
				this.saving = false
			}
		},
		// 스탬프 지점 순서 이동 (dir: -1 위로, 1 아래로)
		moveGroup(index, dir) {
			const target = index + dir
			if (target < 0 || target >= this.form.groups.length) return
			const list = this.form.groups
			const moved = list.splice(index, 1)[0]
			list.splice(target, 0, moved)
		},
		// ===== 참여 현황 =====
		openStats() {
			const today = this.$moment().format('YYYY-MM-DD')
			this.statsForm = {
				groupcode: this.groupcodeItems.includes(this.statsForm.groupcode) ? this.statsForm.groupcode : this.groupcodeItems[0] || '',
				from: this.statsForm.from || today,
				to: this.statsForm.to || today,
			}
			this.statsRows = []
			this.statsDetailRows = []
			this.statsDialog = true
			if (this.statsForm.groupcode) this.fetchStats()
		},
		async fetchStats() {
			if (!this.statsForm.groupcode) {
				alert('행사 코드를 선택해주세요.')
				return
			}
			this.statsLoading = true
			try {
				const res = await this.$store.dispatch('stampStats', {
					groupcode: this.statsForm.groupcode,
					from: this.statsForm.from,
					to: this.statsForm.to,
					detail: 1,
				})
				const data = res.data || {}
				if (data.result !== 'ok') {
					alert('참여 현황을 불러오지 못했습니다.')
					return
				}
				this.statsRows = this.buildStatsRows(data)
				this.statsDetailRows = data.rows || []
			} catch (err) {
				console.log({ err })
				alert('참여 현황 조회 중 오류가 발생하였습니다.')
			} finally {
				this.statsLoading = false
			}
		},
		// 일자 × 투어 행 + 투어별 합계 행 구성
		buildStatsRows(data) {
			const keys = ['participants', 'completed', 'finished', 'draws', 'winners', 'receipts']
			const rows = []
			const totals = {}
			for (const day of data.days || []) {
				for (const tour of data.tours || []) {
					const s = day.tours[tour.code]
					if (!s) continue
					rows.push({
						date: day.date,
						title: tour.title + (tour.type === 'stamp-auto' ? ' (현장 참여)' : ''),
						groupText: tour.groups.map((g, i) => `${g} ${s.groupCounts[i] || 0}`).join(' · '),
						...keys.reduce((o, k) => ({ ...o, [k]: s[k] || 0 }), {}),
					})
					if (!totals[tour.code]) {
						totals[tour.code] = { tour, groupCounts: tour.groups.map(() => 0), ...keys.reduce((o, k) => ({ ...o, [k]: 0 }), {}) }
					}
					keys.forEach(k => (totals[tour.code][k] += s[k] || 0))
					s.groupCounts.forEach((c, i) => (totals[tour.code].groupCounts[i] += c || 0))
				}
			}
			// 조회 기간이 2일 이상이면 투어별 합계(연인원) 행 추가
			if ((data.days || []).length > 1) {
				for (const tour of data.tours || []) {
					const t = totals[tour.code]
					if (!t) continue
					rows.push({
						isTotal: true,
						date: '합계(연인원)',
						title: tour.title + (tour.type === 'stamp-auto' ? ' (현장 참여)' : ''),
						groupText: tour.groups.map((g, i) => `${g} ${t.groupCounts[i]}`).join(' · '),
						...keys.reduce((o, k) => ({ ...o, [k]: t[k] }), {}),
					})
				}
			}
			return rows
		},
		downloadStatsExcel() {
			const XLSX = require('xlsx')
			const wb = XLSX.utils.book_new()
			const summary = this.statsRows.map(r => ({
				일자: r.date,
				투어: r.title,
				참여자: r.participants,
				'지점별 스탬프': r.groupText,
				완주: r.completed,
				참가완료: r.finished,
				추첨: r.draws,
				당첨: r.winners,
				'경품 수령': r.receipts,
			}))
			XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), '요약')
			if (this.statsDetailRows.length > 0) {
				const detail = this.statsDetailRows.map(r => ({
					일자: r.date,
					투어: r.tourTitle,
					이름: r.name,
					연락처: r.phone,
					스탬프: `${r.stamps}/${r.total}`,
					완주: r.complete ? 'O' : '',
					참가완료: r.finished ? 'O' : '',
					당첨경품: r.product,
					수령: r.receipt,
				}))
				XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), '참여자')
			}
			XLSX.writeFile(wb, `스탬프투어_참여현황_${this.statsForm.groupcode}_${this.statsForm.from}_${this.statsForm.to}.xlsx`)
		},
		async removeTour(item) {
			if (!confirm(`[${item.title}] 투어를 삭제하시겠습니까?`)) return
			try {
				await this.$store.dispatch('updateStampTour', {
					id: item.id,
					title: item.title,
					type: item.type,
					status: 9,
				})
				await this.fetchTours()
			} catch (err) {
				console.log({ err })
				alert('삭제 중 오류가 발생하였습니다.')
			}
		},
		// 고객 참여 페이지 URL (스탬프 투어 가이드/참여 host: https://here.re.kr)
		entryUrl(item) {
			const base = process.env.VUE_APP_CUSTOMER_URL + '/stamp/' + item.groupcode
			if (item.type === 'quiz') {
				return base + '/quiz/' + (item.code || '')
			}
			return base
		},
		// 참여용 QR (행사 진입 QR - 고객 페이지 URL)
		openEntryQR(item) {
			this.QRDialogData = {
				dialog: true,
				code: this.entryUrl(item),
				title: item.groupcode + '_참여QR',
			}
		},
		// 고객 참여 페이지 새 탭으로 열기
		openEntryPage(item) {
			window.open(this.entryUrl(item), '_blank')
		},
		// 고객 참여 URL 클립보드 복사
		copyEntryUrl(item) {
			const url = this.entryUrl(item)
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard
					.writeText(url)
					.then(() => alert('참여 URL이 복사되었습니다.\n' + url))
					.catch(() => alert(url))
			} else {
				const ta = document.createElement('textarea')
				ta.value = url
				document.body.appendChild(ta)
				ta.select()
				document.execCommand('copy')
				document.body.removeChild(ta)
				alert('참여 URL이 복사되었습니다.\n' + url)
			}
		},
		// 스탬프 지점별 QR
		openGroupQR(item) {
			this.groupQRList = (item.reserveday_groups || []).filter(g => g.useYn)
			this.groupQRTourTitle = item.title
			if (this.groupQRList.length === 0) {
				alert('노출 중인 스탬프 지점이 없습니다.')
				return
			}
			this.groupQRDialog = true
		},
		showGroupQR(g) {
			this.QRDialogData = {
				dialog: true,
				code: g.qrcode,
				title: this.groupQRTourTitle + '_' + g.title,
			}
		},
		// ===== 공지사항(안내) 관리 =====
		openNoticeManager() {
			if (!this.selectedBusiness) {
				alert('먼저 사업지를 선택해주세요.')
				return
			}
			this.resetNoticeForm()
			this.noticeDialog = true
			this.fetchNotices()
		},
		async fetchNotices() {
			this.noticeLoading = true
			try {
				const res = await this.$store.dispatch('stampNotices', { business: this.selectedBusiness.id })
				this.notices = res.data.stampNotices
			} catch (err) {
				console.log({ err })
			} finally {
				this.noticeLoading = false
			}
		},
		resetNoticeForm() {
			this.noticeForm = this.emptyNoticeForm()
		},
		editNotice(item) {
			this.noticeForm = {
				id: item.id,
				title: item.title,
				content: item.content,
				useYn: item.useYn,
				sort: item.sort || 0,
				images: (item.images || []).map(m => ({ ...m })),
				files: (item.files || []).map(m => ({ ...m })),
				newImageFiles: [],
				newFileFiles: [],
			}
		},
		// 기존 첨부 제거 (저장 시 반영)
		removeExistingImage(idx) {
			this.noticeForm.images.splice(idx, 1)
		},
		removeExistingFile(idx) {
			this.noticeForm.files.splice(idx, 1)
		},
		mediaUrl(url) {
			if (!url) return ''
			return /^https?:\/\//.test(url) ? url : this.$store.state.backServer + url
		},
		async saveNotice() {
			if (!this.noticeForm.title) {
				alert('공지 제목을 입력해주세요.')
				return
			}
			this.noticeSaving = true
			try {
				// 새로 선택한 이미지/파일 업로드 → id 확보 후 기존 id와 합침
				const uploadIds = async files => {
					const ids = []
					for (const f of files || []) {
						const up = await this.$store.dispatch('uploadRest', { file: f })
						ids.push(up.id)
					}
					return ids
				}
				const newImageIds = await uploadIds(this.noticeForm.newImageFiles)
				const newFileIds = await uploadIds(this.noticeForm.newFileFiles)
				const imageIds = [...this.noticeForm.images.map(m => m.id), ...newImageIds]
				const fileIds = [...this.noticeForm.files.map(m => m.id), ...newFileIds]

				if (this.noticeForm.id) {
					await this.$store.dispatch('updateStampNotice', {
						id: this.noticeForm.id,
						title: this.noticeForm.title,
						content: this.noticeForm.content,
						useYn: this.noticeForm.useYn,
						sort: Number(this.noticeForm.sort) || 0,
						images: imageIds,
						files: fileIds,
					})
				} else {
					await this.$store.dispatch('createStampNotice', {
						business: this.selectedBusiness.id,
						groupcode: this.selectedBusiness.groupcode || '',
						title: this.noticeForm.title,
						content: this.noticeForm.content,
						useYn: this.noticeForm.useYn,
						sort: Number(this.noticeForm.sort) || 0,
						images: imageIds,
						files: fileIds,
					})
				}
				this.resetNoticeForm()
				await this.fetchNotices()
			} catch (err) {
				console.log({ err })
				alert('공지 저장 중 오류가 발생하였습니다.')
			} finally {
				this.noticeSaving = false
			}
		},
		async removeNotice(item) {
			if (!confirm(`[${item.title}] 공지를 삭제하시겠습니까?`)) return
			try {
				await this.$store.dispatch('deleteStampNotice', { id: item.id })
				if (this.noticeForm.id === item.id) this.resetNoticeForm()
				await this.fetchNotices()
			} catch (err) {
				console.log({ err })
				alert('공지 삭제 중 오류가 발생하였습니다.')
			}
		},
		// ===== 행사 안내(로그인 팝업) 관리 =====
		emptyFestivalForm() {
			return {
				id: null,
				title: '',
				dateText: '',
				link: '',
				useYn: true,
				sort: 0,
				image: null, // 기존 이미지 {id,name,url}
				newImageFile: null, // 새로 선택한 파일
				imagePreview: '', // 미리보기
			}
		},
		openFestivalManager() {
			if (!this.selectedBusiness) {
				alert('먼저 사업지를 선택해주세요.')
				return
			}
			this.resetFestivalForm()
			this.festivalDialog = true
			this.fetchFestivals()
		},
		async fetchFestivals() {
			this.festivalLoading = true
			try {
				const res = await this.$store.dispatch('stampFestivals', { business: this.selectedBusiness.id })
				this.festivals = res.data.stampFestivals
			} catch (err) {
				console.log({ err })
			} finally {
				this.festivalLoading = false
			}
		},
		resetFestivalForm() {
			this.festivalForm = this.emptyFestivalForm()
		},
		editFestival(item) {
			this.festivalForm = {
				id: item.id,
				title: item.title,
				dateText: item.dateText || '',
				link: item.link || '',
				useYn: item.useYn,
				sort: item.sort || 0,
				image: item.image ? { ...item.image } : null,
				newImageFile: null,
				imagePreview: item.image ? this.mediaUrl(item.image.url) : '',
			}
		},
		onFestivalImageChange(file) {
			if (file) this.festivalForm.imagePreview = URL.createObjectURL(file)
		},
		async saveFestival() {
			if (!this.festivalForm.title) {
				alert('행사 제목을 입력해주세요.')
				return
			}
			this.festivalSaving = true
			try {
				// 새 이미지 업로드 → id 확보 (없으면 기존 유지)
				let imageId = this.festivalForm.image ? this.festivalForm.image.id : null
				if (this.festivalForm.newImageFile) {
					const up = await this.$store.dispatch('uploadRest', { file: this.festivalForm.newImageFile })
					imageId = up.id
				}
				if (this.festivalForm.id) {
					await this.$store.dispatch('updateStampFestival', {
						id: this.festivalForm.id,
						title: this.festivalForm.title,
						dateText: this.festivalForm.dateText,
						link: this.festivalForm.link,
						useYn: this.festivalForm.useYn,
						sort: Number(this.festivalForm.sort) || 0,
						image: imageId,
					})
				} else {
					await this.$store.dispatch('createStampFestival', {
						business: this.selectedBusiness.id,
						groupcode: this.selectedBusiness.groupcode || '',
						title: this.festivalForm.title,
						dateText: this.festivalForm.dateText,
						link: this.festivalForm.link,
						useYn: this.festivalForm.useYn,
						sort: Number(this.festivalForm.sort) || 0,
						image: imageId,
					})
				}
				this.resetFestivalForm()
				await this.fetchFestivals()
			} catch (err) {
				console.log({ err })
				alert('행사 안내 저장 중 오류가 발생하였습니다.')
			} finally {
				this.festivalSaving = false
			}
		},
		async removeFestival(item) {
			if (!confirm(`[${item.title}] 행사 안내를 삭제하시겠습니까?`)) return
			try {
				await this.$store.dispatch('deleteStampFestival', { id: item.id })
				if (this.festivalForm.id === item.id) this.resetFestivalForm()
				await this.fetchFestivals()
			} catch (err) {
				console.log({ err })
				alert('행사 안내 삭제 중 오류가 발생하였습니다.')
			}
		},
	},
}
</script>

<style lang="scss" scoped>
::v-deep .stamp-stats-total td {
	font-weight: 700;
	background: #f3f6fb;
}
.stamp-admin-title {
	font-size: 20px;
	font-weight: 700;
}
.quiz-q-row {
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	background: #fafafa;
}
.notice-content-cell {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	white-space: pre-line;
	font-size: 12px;
	color: #555;
}
.notice-thumb {
	position: relative;
	width: 72px;
	height: 72px;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	overflow: hidden;
	.notice-thumb-del {
		position: absolute;
		top: -6px;
		right: -6px;
		background: #fff;
	}
}
// 공지 목록 테이블: 헤더/버튼 셀 줄바꿈 방지
.notice-table {
	::v-deep th {
		white-space: nowrap;
	}
	::v-deep td.text-start:last-child,
	::v-deep td:nth-child(4) {
		white-space: nowrap;
	}
}
</style>
